<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      style="background: rgba(0, 0, 0, 0.5); z-index: 99999"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">管理幻燈片 (Manage Slides)</h5>
            <button type="button" class="btn-close text-dark" @click="close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body" style="max-height: 70vh; overflow-y: auto">
            <!-- Slide List -->
            <div v-if="!isEditing" class="slide-list">
              <!-- Tabs for Desktop/Mobile -->
              <div class="d-flex gap-2 mb-4 border-bottom pb-3">
                <button
                  @click="activeTab = 'desktop'"
                  class="btn btn-sm px-4 py-2 rounded-pill transition-all"
                  :class="
                    activeTab === 'desktop'
                      ? 'btn-dark shadow-sm'
                      : 'btn-outline-secondary bg-light'
                  "
                >
                  <i class="fas fa-desktop me-2"></i>電腦版 (Desktop)
                </button>
                <button
                  @click="activeTab = 'mobile'"
                  class="btn btn-sm px-4 py-2 rounded-pill transition-all"
                  :class="
                    activeTab === 'mobile'
                      ? 'btn-dark shadow-sm'
                      : 'btn-outline-secondary bg-light'
                  "
                >
                  <i class="fas fa-mobile-alt me-2"></i>手機版 (Mobile)
                </button>
              </div>

              <div class="d-flex justify-content-end mb-3">
                <button
                  class="btn bg-gradient-success btn-sm"
                  @click="createNewSlide"
                >
                  <i class="fas fa-plus me-1"></i>
                  {{
                    activeTab === "desktop"
                      ? "新增電腦版幻燈片"
                      : "新增手機版幻燈片"
                  }}
                </button>
              </div>

              <div class="table-responsive">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th
                        class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                      >
                        媒體/標題
                      </th>
                      <th
                        class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                      >
                        連結/按鈕
                      </th>
                      <th
                        class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                      >
                        排序
                      </th>
                      <th
                        class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                      >
                        狀態
                      </th>
                      <th class="text-secondary opacity-7"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="slide in filteredSlides" :key="slide.id">
                      <td>
                        <div class="d-flex px-2 py-1">
                          <div>
                            <!-- Desktop Preview -->
                            <template v-if="activeTab === 'desktop'">
                              <img
                                v-if="
                                  slide.media_type === 'image' &&
                                  slide.media_url
                                "
                                :src="slide.media_url"
                                class="avatar avatar-sm me-3 border-radius-lg"
                                alt="slide"
                              />
                              <span
                                v-else-if="slide.media_type === 'video'"
                                class="avatar avatar-sm me-3 border-radius-lg bg-gradient-dark d-flex align-items-center justify-content-center"
                              >
                                <i class="fas fa-video text-white"></i>
                              </span>
                              <span
                                v-else
                                class="avatar avatar-sm me-3 border-radius-lg bg-gray-200"
                              ></span>
                            </template>
                            <!-- Mobile Preview -->
                            <template v-else>
                              <!-- Uses standard media_url now because it's a separate record -->
                              <img
                                v-if="
                                  slide.media_url && !isVideo(slide.media_url)
                                "
                                :src="slide.media_url"
                                class="avatar avatar-lg me-3 border-radius-lg"
                                style="
                                  width: 36px;
                                  height: 60px;
                                  object-fit: cover;
                                "
                                alt="slide"
                              />
                              <span
                                v-else-if="
                                  slide.media_url && isVideo(slide.media_url)
                                "
                                class="avatar avatar-sm me-3 border-radius-lg bg-gradient-dark d-flex align-items-center justify-content-center"
                              >
                                <i class="fas fa-video text-white"></i>
                              </span>
                              <span
                                v-else
                                class="avatar avatar-sm me-3 border-radius-lg bg-gray-200"
                              ></span>
                            </template>
                          </div>
                          <div
                            class="d-flex flex-column justify-content-center"
                          >
                            <h6 class="mb-0 text-sm">
                              {{ slide.title || "No Title" }}
                            </h6>
                            <p class="text-xs text-secondary mb-0">
                              {{ slide.media_type }}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <p class="text-xs font-weight-bold mb-0">
                          {{ slide.link || "-" }}
                        </p>
                        <p class="text-xs text-secondary mb-0">
                          {{ slide.cta_text || "-" }}
                        </p>
                      </td>
                      <td class="align-middle text-center">
                        <span class="text-secondary text-xs font-weight-bold">{{
                          slide.sort_order
                        }}</span>
                      </td>
                      <td class="align-middle text-center text-sm">
                        <span
                          class="badge badge-sm"
                          :class="
                            slide.is_active
                              ? 'bg-gradient-success'
                              : 'bg-gradient-secondary'
                          "
                        >
                          {{ slide.is_active ? "Active" : "Inactive" }}
                        </span>
                      </td>
                      <td class="align-middle">
                        <button
                          class="btn btn-link text-secondary font-weight-bold text-xs me-3 mb-0"
                          @click="editSlide(slide)"
                        >
                          編輯
                        </button>
                        <button
                          class="btn btn-link text-danger font-weight-bold text-xs mb-0"
                          @click="deleteSlide(slide.id)"
                        >
                          刪除
                        </button>
                      </td>
                    </tr>
                    <tr v-if="filteredSlides.length === 0">
                      <td colspan="5" class="text-center py-4">
                        <span class="text-secondary text-sm"
                          >目前沒有{{
                            activeTab === "desktop" ? "電腦版" : "手機版"
                          }}幻燈片</span
                        >
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Edit Form -->
            <div v-else class="slide-form">
              <div
                class="d-flex justify-content-between align-items-center mb-3"
              >
                <h6 class="mb-0">
                  {{
                    form.id
                      ? "編輯幻燈片"
                      : activeTab === "desktop"
                      ? "新增電腦版幻燈片"
                      : "新增手機版幻燈片"
                  }}
                </h6>
                <button
                  class="btn btn-outline-secondary btn-sm"
                  @click="isEditing = false"
                >
                  返回列表
                </button>
              </div>

              <div class="row">
                <!-- Row 1: Media Type & Sort Order -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-control-label"
                      >媒體類型 (Media Type)</label
                    >
                    <select class="form-control" v-model="form.media_type">
                      <option value="image">圖片 (Image)</option>
                      <option value="video">影片 (Video)</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-control-label">排序 (Sort Order)</label>
                    <input
                      type="number"
                      class="form-control"
                      v-model.number="form.sort_order"
                    />
                  </div>
                </div>

                <!-- Row 2: Media Uploads -->
                <!-- Image Mode -->
                <!-- Image Mode -->
                <template v-if="form.media_type === 'image'">
                  <div class="col-md-12">
                    <div class="form-group mb-3">
                      <label class="form-control-label">
                        {{
                          form.device === "mobile"
                            ? "手機圖片 (Mobile Image - 9:16)"
                            : "桌面圖片 (Desktop Image - 16:9)"
                        }}
                      </label>

                      <div class="mb-2" v-if="form.media_url">
                        <img
                          :src="form.media_url"
                          class="img-fluid border-radius-lg border"
                          style="
                            max-height: 200px;
                            width: auto;
                            object-fit: cover;
                            margin: 0 auto;
                            display: block;
                          "
                        />
                      </div>
                      <input
                        type="file"
                        ref="fileInput"
                        class="d-none"
                        accept="image/*,.heic,.heif"
                        @change="(e) => onFileSelected(e, form.device)"
                      />
                    </div>
                    <div class="d-flex gap-2">
                      <button
                        class="btn btn-outline-primary btn-sm mb-0 w-100"
                        @click="$refs.fileInput.click()"
                      >
                        <i class="fas fa-upload me-1"></i> 上傳圖片
                      </button>
                      <button
                        v-if="form.media_url"
                        class="btn btn-outline-danger btn-sm mb-0"
                        @click="removeImage('desktop')"
                        title="移除圖片"
                      >
                        <!-- Note: removeImage('desktop') just clears media_url, which is what we want for both devices now since we unified the field usage -->
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                </template>

                <!-- Video Mode -->
                <template v-if="form.media_type === 'video'">
                  <div class="col-md-12">
                    <div class="form-group mb-3">
                      <label class="form-control-label">
                        {{
                          form.device === "mobile"
                            ? "手機影片 (Mobile Video)"
                            : "桌面影片 (Desktop Video)"
                        }}
                      </label>
                      <div class="mb-2" v-if="form.media_url">
                        <video
                          :src="form.media_url"
                          class="img-fluid border-radius-lg border"
                          style="
                            max-height: 300px;
                            width: auto;
                            margin: 0 auto;
                            display: block;
                          "
                          controls
                        ></video>
                      </div>
                      <input
                        type="file"
                        ref="videoInput"
                        class="d-none"
                        accept="video/*"
                        @change="(e) => onVideoSelected(e, form.device)"
                      />
                      <div class="d-flex gap-2">
                        <button
                          class="btn btn-outline-info btn-sm mb-0"
                          @click="$refs.videoInput.click()"
                        >
                          <i class="fas fa-video me-1"></i> 上傳影片
                        </button>
                        <button
                          v-if="form.media_url"
                          class="btn btn-outline-danger btn-sm mb-0"
                          @click="removeImage('desktop')"
                        >
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-12">
                    <small class="text-muted"
                      >支援 MP4, WebM 等格式 (最大 500MB)</small
                    >
                  </div>
                </template>

                <!-- Row 3: Title -->
                <div class="col-md-12">
                  <div class="form-group mb-3">
                    <label class="form-control-label">標題 (Title)</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="form.title"
                    />
                  </div>
                </div>

                <!-- Row 4: Subtitle & Link -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-control-label">副標題 (Subtitle)</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="form.subtitle"
                    />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-control-label">連結 (Link)</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="form.link"
                      placeholder="/products"
                    />
                  </div>
                </div>

                <!-- Row 5: CTA & Active -->
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-control-label"
                      >按鈕文字 (CTA Text)</label
                    >
                    <input
                      type="text"
                      class="form-control"
                      v-model="form.cta_text"
                    />
                  </div>
                </div>
                <div class="col-md-6 d-flex align-items-center">
                  <div class="form-check form-switch ps-0 mt-3">
                    <input
                      class="form-check-input ms-auto"
                      type="checkbox"
                      v-model="form.is_active"
                      id="activeSwitch"
                    />
                    <label
                      class="form-check-label text-body ms-3 text-truncate w-80 mb-0"
                      for="activeSwitch"
                      >啟用 (Active)</label
                    >
                  </div>
                </div>

                <!-- Row 6: Description -->
                <div class="col-md-12">
                  <div class="form-group mb-3">
                    <label class="form-control-label">描述 (Description)</label>
                    <textarea
                      class="form-control"
                      rows="3"
                      v-model="form.description"
                    ></textarea>
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-end mt-3">
                <button
                  class="btn btn-primary"
                  @click="saveSlide"
                  :disabled="saving"
                >
                  {{ saving ? "Saving..." : "儲存幻燈片" }}
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer" v-if="!isEditing">
            <button type="button" class="btn btn-secondary" @click="close">
              關閉
            </button>
          </div>
        </div>
      </div>

      <!-- Cropper Modal -->
      <ImageCropperModal
        :show="showCropper"
        :image="tempImageSrc"
        :aspectRatio="currentAspectRatio"
        @close="showCropper = false"
        @save="handleCrop"
      />
    </div>
  </Teleport>
</template>

<script>
import request from "@/utils/request";
import ImageCropperModal from "../../components/ImageCropperModal.vue";
import Swal from "sweetalert2";

export default {
  name: "HeroSlideManager",
  components: {
    ImageCropperModal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: "desktop", // 'desktop' | 'mobile'
      slides: [],
      isEditing: false,
      saving: false,
      form: {
        id: null,
        media_type: "image",
        media_url: "",
        title: "",
        subtitle: "",
        description: "",
        link: "",
        cta_text: "",
        sort_order: 0,
        is_active: true,
      },
      showCropper: false,
      tempImageSrc: "",
      currentAspectRatio: 16 / 9,
      cropTarget: "desktop", // 'desktop' or 'mobile'
    };
  },
  computed: {
    filteredSlides() {
      if (this.activeTab === "desktop") {
        return this.slides.filter((s) => s.device === "desktop" || !s.device);
      } else {
        return this.slides.filter((s) => s.device === "mobile");
      }
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.fetchSlides();
        this.isEditing = false;
        // Reset to first tab or keep? Keep is fine.
      }
    },
  },
  mounted() {
    if (this.show) {
      this.fetchSlides();
    }
  },
  methods: {
    close() {
      this.$emit("close");
    },
    async fetchSlides() {
      try {
        const response = await request.get("/api/admin/hero-slides");
        this.slides = response.data.data || response.data;
      } catch (error) {
        console.error("Failed to fetch slides:", error);
        Swal.fire("Error", "無法載入幻燈片", "error");
      }
    },
    createNewSlide() {
      this.form = {
        id: null,
        media_type: "image",
        media_url: "",
        title: "",
        subtitle: "",
        description: "",
        link: "",
        cta_text: "Shop Now",
        sort_order: this.slides.length,
        is_active: true,
        device: this.activeTab === "mobile" ? "mobile" : "desktop",
      };
      this.isEditing = true;
    },
    editSlide(slide) {
      this.form = { ...slide };
      // Ensure defaults
      if (!this.form.media_type) this.form.media_type = "image";
      this.isEditing = true;
    },
    async deleteSlide(id) {
      const result = await Swal.fire({
        title: "確定刪除?",
        text: "此動作無法復原",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "是的, 刪除!",
        cancelButtonText: "取消",
      });

      if (result.isConfirmed) {
        try {
          await request.delete(`/api/admin/hero-slides/${id}`);
          await this.fetchSlides();
          Swal.fire("Deleted!", "幻燈片已刪除", "success");
        } catch (error) {
          console.error(error);
          Swal.fire("Error", "刪除失敗", "error");
        }
      }
    },
    async saveSlide() {
      this.saving = true;
      try {
        // Strict Separation: No clearing needed because we only edit what's relevant.
        // But for safety, we can ensure the 'other' URL is empty if we want to be super clean,
        // though the backend should handle it or we just ignore it.
        // Actually, let's just send what we have.

        // Ensure device is set
        if (!this.form.device) {
          this.form.device = this.activeTab === "mobile" ? "mobile" : "desktop";
        }

        if (this.form.id) {
          await request.put(
            `/api/admin/hero-slides/${this.form.id}`,
            this.form
          );
        } else {
          await request.post("/api/admin/hero-slides", this.form);
        }
        await this.fetchSlides();
        this.isEditing = false;
        Swal.fire("Success", "儲存成功", "success");
      } catch (error) {
        console.error(error);
        Swal.fire("Error", "儲存失敗", "error");
      } finally {
        this.saving = false;
      }
    },
    onFileSelected(e, target = "desktop") {
      const file = e.target.files[0];
      if (!file) return;

      this.cropTarget = target;
      if (target === "mobile") {
        this.currentAspectRatio = 9 / 16;
      } else {
        this.currentAspectRatio = 16 / 9;
      }

      // Convert to data URL for cropper
      const reader = new FileReader();
      reader.onload = (event) => {
        this.tempImageSrc = event.target.result;
        this.showCropper = true;
        // Clear input
        e.target.value = "";
      };
      reader.readAsDataURL(file);
    },
    async handleCrop(blob) {
      this.showCropper = false;
      if (!blob) {
        Swal.fire("Error", "Cropping failed (no image data)", "error");
        return;
      }

      // const suffix = this.cropTarget === "mobile" ? "_mobile" : "";
      const suffix = "";
      const formData = new FormData();
      formData.append("file", blob, `slide_image${suffix}.jpg`);

      try {
        // Show loading
        Swal.showLoading();
        const res = await request.post("/api/admin/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.close();
        if (res.data.success) {
          // Unified field
          this.form.media_url = res.data.url;
        } else {
          Swal.fire("Error", "上傳失敗", "error");
        }
      } catch (e) {
        Swal.close();
        console.error(e);
        Swal.fire(
          "Error",
          "上傳失敗: " + (e.response?.data?.message || e.message),
          "error"
        );
      }
    },
    async onVideoSelected(e) {
      const input = e.target;
      const file = input.files[0];
      if (!file) return;

      if (file.size > 500 * 1024 * 1024) {
        Swal.fire("Error", "檔案過大，請上傳小於 500MB 的影片", "error");
        input.value = "";
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      try {
        Swal.fire({
          title: "Uploading Video...",
          text: "Please wait, this may take a moment.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        const res = await request.post("/api/admin/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        Swal.close();

        if (res.data.success) {
          // Always set to media_url now, as we assume strict separate records
          this.form.media_url = res.data.url;

          Swal.fire({
            icon: "success",
            title: "Upload Successful",
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          Swal.fire("Error", "影片上傳失敗", "error");
        }
      } catch (e) {
        Swal.close();
        console.error(e);
        Swal.fire(
          "Error",
          "上傳錯誤: " + (e.response?.data?.message || e.message),
          "error"
        );
      } finally {
        input.value = "";
      }
    },
    removeImage() {
      this.form.media_url = "";
    },
    isVideo(url) {
      if (!url) return false;
      const extension = url.split(".").pop().toLowerCase();
      return ["mp4", "webm", "ogg", "mov"].includes(extension);
    },
  },
};
</script>

<style scoped>
.avatar-sm {
  width: 36px;
  height: 36px;
  object-fit: cover;
}
</style>
